import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope } from "@/api/errors";
import type { paths } from "@/api/schema.generated";

/** The capabilities the availability route answers for, as its schema declares them. */
export type AiCapabilityId = NonNullable<
  NonNullable<paths["/v1/ai/availability"]["get"]["parameters"]["query"]>["capability_id"]
>;

/** The build switch and fresh authenticated response both have to allow AI. */
export function isSupportAssistantEnabled(): boolean {
  return import.meta.env["VITE_SUPPORT_AI_ASSISTANT_ENABLED"] !== "off";
}

export function useAiPrincipalKey(): readonly string[] {
  const auth = useAuth();
  const profile = auth.user?.profile;
  return ["ai", typeof profile?.iss === "string" ? profile.iss : "", typeof profile?.tenant_id === "string" ? profile.tenant_id : "", profile?.sub ?? ""];
}

async function fetchAvailability(signal: AbortSignal, capabilityId: AiCapabilityId | null): Promise<boolean> {
  const result = await unwrapEnvelope(platformGraph.GET("/v1/ai/availability", {
    ...(capabilityId !== null && { params: { query: { capability_id: capabilityId } } }),
    signal: AbortSignal.any([signal, AbortSignal.timeout(5_000)]),
  }));
  return result.available === true;
}

/**
 * Whether ONE AI capability may be offered right now. `null` asks the route's
 * default (the help assistant) and keeps that launcher's cache key unchanged.
 * Every capability key sits under the principal key, so the AI settings
 * screen's identity invalidation refreshes all of them at once.
 */
export function useAiCapabilityAvailability(capabilityId: AiCapabilityId | null, buildEnabled = true): boolean {
  const identity = useAiPrincipalKey();
  const enabled = buildEnabled && identity[2] !== "" && identity[3] !== "";
  const query = useQuery({
    queryKey: capabilityId === null ? [...identity, "availability"] : [...identity, "availability", capabilityId],
    queryFn: ({ signal }) => fetchAvailability(signal, capabilityId),
    enabled, retry: false, staleTime: 0, gcTime: 0,
    refetchInterval: 10_000, refetchIntervalInBackground: true,
    refetchOnMount: "always", refetchOnWindowFocus: "always",
  });
  return enabled && query.isSuccess && query.data === true && Date.now() - query.dataUpdatedAt < 20_000;
}

export function useAiAvailability(): boolean {
  return useAiCapabilityAvailability(null, isSupportAssistantEnabled());
}
